import { WorkModel } from '@Entities/work/work'
import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { IPIValidator } from '@App/helpers/validate'
import SearchResult from '@Components/WorkRegistrationSubs/SearchResult'
import './index.scss'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { setItemToArrayByIndex } from '@App/helpers/work-entities/removers'
import { PublisherSideBarState } from '@App/enum/publisher-side-bar-state'
import { formatName } from '@App/helpers/format-name'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { WriterModel } from '@Entities/work/writer-model'

interface WriterSideBarProps {
    data: OriginalPublisherModel
}

const WriterSideBar = ({ data }: WriterSideBarProps) => {
    const { workStore, popupStore } = useContext<RootStore>(StoresContext)

    const [workWriters, setWorkWriters] = useState<WriterModel[]>([])

    const getWorkWriters = () => {
        if (!workStore.workDraftLocal) {
            return
        }

        const linkedWriters = data.writers
        const newWorkWriters = workStore.workDraftLocal.writers
        if (linkedWriters.length) {
            setWorkWriters(
                newWorkWriters.filter((newWorkWriter: WriterModel) => {
                    const coincidence = !!linkedWriters.filter(
                        (linkedWriter: WriterModel) => linkedWriter.localId === newWorkWriter.localId
                    ).length
                    if (coincidence) {
                        return
                    }
                    return newWorkWriter
                })
            )
            return
        }
        setWorkWriters(newWorkWriters)
    }

    useEffect(() => {
        getWorkWriters()
    }, [])

    const addWriter = (addingWriter: WriterModel) => {
        const workDraftFromLocal = workStore.workDraftLocal

        const {
            id,
            localId,
            publisherName,
            ipiNumber,
            amend,
            publisherShare,
            administratorPublishers,
            writers,
            hfaPublisherNumber,
        } = data

        if (!workDraftFromLocal || !localId || !addingWriter) {
            return
        }

        const originalPublishers = workDraftFromLocal.originalPublishers
        const indexOfOP = originalPublishers.map((op: OriginalPublisherModel) => op.localId).indexOf(localId)

        const linkedWriters: WriterModel[] = [...writers, addingWriter]

        const updatedOriginalPublisher: OriginalPublisherModel = {
            ...data,
            id: id,
            localId: localId,
            publisherName: publisherName,
            ipiNumber: ipiNumber,
            amend: amend,
            publisherShare: publisherShare,
            administratorPublishers: administratorPublishers,
            writers: linkedWriters,
            hfaPublisherNumber: hfaPublisherNumber,
        }

        const updatedOPArray = setItemToArrayByIndex(originalPublishers, +indexOfOP, updatedOriginalPublisher)

        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            originalPublishers: updatedOPArray,
        }

        workStore.setWorkDraftToLocal(updatedWorkDraft)
        const newWorkWriters = workWriters.filter(
            (workWriter: WriterModel) => workWriter.localId !== addingWriter.localId
        )
        setWorkWriters(newWorkWriters)
        popupStore.setPublisherSidebarState(PublisherSideBarState.empty)
    }

    const isValidIPI = (ipiNumber: string | null) => (ipiNumber ? IPIValidator(ipiNumber, true) : true)

    const workWriterRoles = (workWriter: WriterModel) =>
        isValidIPI(workWriter.ipiNumber) && workWriter.ipiNumber
            ? [{ name: 'IPI Number', value: workWriter.ipiNumber }]
            : []

    return (
        <div className="link-writer-to-op">
            <h2>Link Writer</h2>
            <div>Select one or more writers below to link them to the publisher/administrator listed on this work.</div>

            {workWriters && workWriters.length ? (
                workWriters.map((workWriter: WriterModel, index) => (
                    <SearchResult
                        key={workWriter.ipId ?? index}
                        title={workWriter.lastName ? formatName(workWriter.firstName, workWriter.lastName) : '___'}
                        onAddClicked={() => addWriter(workWriter)}
                        onItemWasAdded={() => false}
                        roles={workWriterRoles(workWriter)}
                        addButtonText="Link writer"
                    />
                ))
            ) : (
                <p>No writers left to add.</p>
            )}
        </div>
    )
}
export default observer(WriterSideBar)
